import { useState } from "react";
import { Users } from "lucide-react";

import { Tabs } from "@/features/power_projects/components/ui";
import { AccessGroupsModal } from "@/features/repository/components/AccessGroupsModal";
import { useAuthStore } from "@/store/auth";

import { twoDriveApi } from "../api";
import { FolderBrowser } from "../components/FolderBrowser";
import { QuotaBar } from "../components/QuotaBar";
import { SharedByMeList } from "../components/SharedByMeList";
import { SharedWithMeList } from "../components/SharedWithMeList";
import { ZipDownloadToast, type ZipJob } from "../components/ZipDownloadToast";
import { useSharedByMe, useSharedWithMe } from "../hooks";
import type { DriveNode } from "../types";

type Tab = "moje" | "udostepnione_mi" | "udostepnione_przeze_mnie";

export function TwoDrivePage() {
  const [tab, setTab] = useState<Tab>("moje");
  const [mojeParent, setMojeParent] = useState<string | null>(null);
  const [sharedParent, setSharedParent] = useState<string | null>(null);
  const [sharedRootName, setSharedRootName] = useState("Udostępnione mi");
  const [zipJobs, setZipJobs] = useState<(ZipJob & { key: string })[]>([]);
  const [showGroups, setShowGroups] = useState(false);
  // Access groups are manageable only by directors/coordinators (backend
  // can_create_access_groups) — same gate as the Repository page button.
  const canManageGroups = useAuthStore(
    (s) => s.user?.role === "director" || s.user?.role === "coordinator",
  );

  const { data: sharedWithMe = [] } = useSharedWithMe();
  const { data: sharedByMe = [] } = useSharedByMe();

  function addZipJob(taskId: string, label: string) {
    setZipJobs((j) => [...j, { key: crypto.randomUUID(), taskId, folderName: label }]);
  }

  async function startZip(node: DriveNode) {
    const res = await twoDriveApi.download(node.id);
    if (res.task_id) addZipJob(res.task_id, node.name);
  }

  async function startZipSelection(ids: string[], label: string) {
    const res = await twoDriveApi.zipNodes(ids);
    if (res.task_id) addZipJob(res.task_id, label);
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto flex flex-col gap-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-mono font-bold uppercase tracking-wider text-[var(--color-text-primary)]">
            TwoDrive
          </h1>
          <p className="text-sm text-[var(--color-text-muted)]">Twoja przestrzeń na pliki w chmurze</p>
        </div>
        {canManageGroups && (
          <button
            onClick={() => setShowGroups(true)}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono uppercase tracking-wider border border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
          >
            <Users size={13} /> Grupy dostępu
          </button>
        )}
      </div>

      <QuotaBar />

      <Tabs<Tab>
        tabs={[
          { key: "moje", label: "Moje pliki" },
          { key: "udostepnione_mi", label: "Udostępnione mi", count: sharedWithMe.length, countTone: "blue" },
          {
            key: "udostepnione_przeze_mnie",
            label: "Udostępnione przeze mnie",
            count: sharedByMe.length,
            countTone: "blue",
          },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === "moje" && (
        <FolderBrowser
          parent={mojeParent}
          rootLabel="Moje pliki"
          onNavigate={setMojeParent}
          onZip={startZip}
          onZipSelection={startZipSelection}
        />
      )}

      {tab === "udostepnione_mi" &&
        (sharedParent === null ? (
          <SharedWithMeList
            onOpenFolder={(id, name) => {
              setSharedParent(id);
              setSharedRootName(name);
            }}
          />
        ) : (
          <FolderBrowser
            parent={sharedParent}
            rootLabel={sharedRootName}
            onNavigate={setSharedParent}
            onHome={() => setSharedParent(null)}
            onZip={startZip}
            onZipSelection={startZipSelection}
          />
        ))}

      {tab === "udostepnione_przeze_mnie" && <SharedByMeList />}

      {showGroups && <AccessGroupsModal onClose={() => setShowGroups(false)} />}

      {/* Folder-zip progress toasts */}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
        {zipJobs.map((j) => (
          <ZipDownloadToast
            key={j.key}
            job={j}
            onDismiss={() => setZipJobs((z) => z.filter((x) => x.key !== j.key))}
          />
        ))}
      </div>
    </div>
  );
}
